import { Component, ElementRef, ViewChild, afterNextRender, OnDestroy, inject, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import * as THREE from 'three';
import { isAuth } from './auth';
import { WwwwwwComponent } from "./bottom";
import { rgb } from 'pdf-lib';
import { MarkdownModule } from 'ngx-markdown';

@Component({
  selector: 'app-www',
  standalone: true,
  imports: [WwwwwwComponent],
  template: `
    <div class="landing-container">
      <canvas #canvasRef></canvas>
      
      <div class="footer-overlay">
        <app-wwwwww></app-wwwwww>
      </div>
    </div>
  `,
  styles: [`
    .landing-container {
      position: relative;
      width: 100%;
      min-height: 100vh;
      overflow-x: hidden;
      background: #020617;
      font-family: system-ui, -apple-system, sans-serif;
    }
    canvas {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: block;
      z-index: 1;
    }
    .footer-overlay {
      position: absolute;
      top: 53.5%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 100%;
      z-index: 10;
      pointer-events: auto;
    }
  `]
})
export class Wwww implements OnDestroy {
  @ViewChild('canvasRef', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;
  auth: any = isAuth();

  private renderer!: THREE.WebGLRenderer;
  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private geometry!: THREE.TorusKnotGeometry;
  private material!: THREE.MeshStandardMaterial;
  private torusKnot!: THREE.Mesh;

  private animationFrameId = 0;
  private ngZone = inject(NgZone);
  private router = inject(Router);

  private mouseX = 0;
  private mouseY = 0;
  private targetX = 0;
  private targetY = 0;

  private boundOnResize = this.onWindowResize.bind(this);
  private boundOnMouseMove = this.onMouseMove.bind(this);

  constructor() {
    afterNextRender(() => {
      this.initScene();
    });
  }

  private initScene() {
    const canvas = this.canvasRef.nativeElement;
    const width = window.innerWidth;
    const height = window.innerHeight;

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
    this.camera.position.z = 5;

    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.8);
    this.scene.add(ambientLight);

    const pointLight = new THREE.PointLight(0x38bdf8, 3);
    pointLight.position.set(5, 5, 5);
    this.scene.add(pointLight);

    const pointLight2 = new THREE.PointLight(0x818cf8, 3);
    pointLight2.position.set(-5, -5, 5);
    this.scene.add(pointLight2);

    this.geometry = new THREE.TorusKnotGeometry(1.2, 0.25, 128, 32);
    this.material = new THREE.MeshStandardMaterial({
      color: 'rgba(23, 9, 94, 1)',
      roughness: 0.1,
      metalness: 0.9,
      wireframe: false
    });

    this.torusKnot = new THREE.Mesh(this.geometry, this.material);
    this.scene.add(this.torusKnot);

    // Adjuntar eventos fuera de Angular para evitar disparar la detección de cambios (Change Detection)
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('resize', this.boundOnResize);
      window.addEventListener('mousemove', this.boundOnMouseMove);

      const animate = () => {
        this.animationFrameId = requestAnimationFrame(animate);

        this.targetX += (this.mouseX - this.targetX) * 0.05;
        this.targetY += (this.mouseY - this.targetY) * 0.05;

        this.torusKnot.rotation.x = this.targetY * 0.5 + Date.now() * 0.0005;
        this.torusKnot.rotation.y = this.targetX * 0.5 + Date.now() * 0.0008;

        this.renderer.render(this.scene, this.camera);
      };
      animate();
    });
  }

  private onMouseMove(event: MouseEvent) {
    this.mouseX = (event.clientX / window.innerWidth) * 2 - 1;
    this.mouseY = -(event.clientY / window.innerHeight) * 2 + 1;
  }

  private onWindowResize() {
    const width = window.innerWidth;
    const height = window.innerHeight;
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  }

  ngOnDestroy() {
    // 1. Cancelar bucle de animación
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }

    // 2. Remover event listeners de la ventana
    window.removeEventListener('resize', this.boundOnResize);
    window.removeEventListener('mousemove', this.boundOnMouseMove);

    // 3. Liberación explicita de memoria GPU de Three.js (Evita fugas de memoria)
    if (this.geometry) {
      this.geometry.dispose();
    }
    if (this.material) {
      this.material.dispose();
    }
    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.forceContextLoss();
    }
  }
}